/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Fade, IconButton } from '@coursera/cds-core';
import { ArrowDownIcon, ArrowUpIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/cds-labs';

// Distance from bottom (in px) below which the button is hidden.
const BOTTOM_THRESHOLD_PX = 50;

type Props = {
  children: React.ReactNode;
  className?: string;
  /** Enables the "Jump to Bottom" button. When true (default), the button
   *  auto-shows when the user scrolls more than 50 px above the bottom. */
  enableJumpToBottom?: boolean;
  /** Called when the "Jump to Bottom" button is clicked, before scrolling. */
  onJumpToBottom?: () => void;
  /** Enables the "Jump to Top" button. When true (default), the button
   *  auto-shows when the user is at the bottom and there is scrollable content above. */
  enableJumpToTop?: boolean;
  /** Called when the "Jump to Top" button is clicked, before scrolling. */
  onJumpToTop?: () => void;
};

export type ChatMessagesContainerRef = {
  /** Scrolls the internal viewport to the bottom (smooth). Scoped to the inner scroll
   *  container — does not bubble to ancestor scroll containers the way `scrollIntoView`
   *  would. */
  scrollToBottom: () => void;
  /** Scrolls an element into view within the inner scroll container. Unlike the native
   *  `Element.scrollIntoView`, this does not bubble to ancestor scroll containers. */
  scrollElementIntoView: (
    element: HTMLElement,
    options?: { behavior?: ScrollBehavior; block?: 'start' | 'end' }
  ) => void;
};

const styles = {
  outerWrapper: css`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `,
  scrollContainer: css`
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    padding-top: var(--cds-spacing-200);
  `,
  contentContainer: css`
    max-width: 656px;
    margin: 0 auto;
  `,
  jumpButtonOverlay: css`
    position: absolute;
    bottom: var(--cds-spacing-300);
    left: 50%;
    transform: translateX(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    @container (max-height: 480px) {
      display: none;
    }
  `,
  jumpButton: css`
    pointer-events: auto;
    box-shadow: var(--cds-elevation-level1);
    border-radius: 50%;
  `,
};

const FADE_TIMEOUT = 150;

/**
 * Scrollable wrapper for chat messages. Renders floating "Jump to Bottom" and
 * optional "Jump to Top" buttons with fade animations.
 *
 * Forwards an imperative `scrollToBottom()` handle so callers can scope auto-scroll
 * to this container instead of relying on `Element.scrollIntoView`, which scrolls
 * every scrollable ancestor (often the wrong one when embedded in a frame).
 */
const ChatMessagesContainer = React.forwardRef<ChatMessagesContainerRef, Props>(
  ({ children, className, enableJumpToBottom = true, onJumpToBottom, enableJumpToTop = true, onJumpToTop }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isNearBottom, setIsNearBottom] = useState(true);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [hasScrollableContent, setHasScrollableContent] = useState(false);

    const handleScroll = useCallback<() => void>(() => {
      const el = scrollRef.current;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setIsNearBottom(distanceFromBottom <= BOTTOM_THRESHOLD_PX);
      setIsAtBottom(distanceFromBottom <= 1);
      setHasScrollableContent(el.scrollHeight - el.clientHeight > BOTTOM_THRESHOLD_PX);
    }, []);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return undefined;
      el.addEventListener('scroll', handleScroll, { passive: true });
      const observer = new ResizeObserver(handleScroll);
      observer.observe(el);
      return () => {
        el.removeEventListener('scroll', handleScroll);
        observer.disconnect();
      };
    }, [handleScroll]);

    const scrollToBottom = useCallback<() => void>(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, []);

    const scrollElementIntoView = useCallback(
      (element: HTMLElement, options?: { behavior?: ScrollBehavior; block?: 'start' | 'end' }) => {
        const container = scrollRef.current;
        if (!container) return;

        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top + container.scrollTop;

        const block = options?.block ?? 'start';
        // Extra clearance so the element isn't hidden behind the header fade gradient
        const SCROLL_MARGIN_TOP = 16;
        const targetScrollTop =
          block === 'end' ? relativeTop + elementRect.height - containerRect.height : relativeTop - SCROLL_MARGIN_TOP;

        container.scrollTo({
          top: targetScrollTop,
          behavior: options?.behavior ?? 'auto',
        });
      },
      []
    );

    const scrollToTop = useCallback<() => void>(() => {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleJumpToBottom = useCallback<() => void>(() => {
      onJumpToBottom?.();
      scrollToBottom();
    }, [onJumpToBottom, scrollToBottom]);

    const handleJumpToTop = useCallback<() => void>(() => {
      onJumpToTop?.();
      scrollToTop();
    }, [onJumpToTop, scrollToTop]);

    React.useImperativeHandle(ref, () => ({ scrollToBottom, scrollElementIntoView }), [
      scrollToBottom,
      scrollElementIntoView,
    ]);

    return (
      <div css={styles.outerWrapper} className={className}>
        <div ref={scrollRef} css={styles.scrollContainer} data-testid="chat-messages-scroll-container">
          <div css={styles.contentContainer}>{children}</div>
        </div>
        <Fade in={enableJumpToTop && isAtBottom && hasScrollableContent} timeout={FADE_TIMEOUT}>
          <div css={styles.jumpButtonOverlay}>
            <IconButton
              hideTooltip
              icon={<ArrowUpIcon />}
              tooltip={_t('Jump to top')}
              size="small"
              variant="secondaryNeutral"
              css={styles.jumpButton}
              onClick={handleJumpToTop}
              aria-label={_t('Jump to top')}
              data-testid="chat-jump-to-top"
            />
          </div>
        </Fade>
        <Fade in={enableJumpToBottom && !isNearBottom} timeout={FADE_TIMEOUT}>
          <div css={styles.jumpButtonOverlay}>
            <IconButton
              hideTooltip
              icon={<ArrowDownIcon />}
              tooltip={_t('Jump to most recent message')}
              size="small"
              variant="secondaryNeutral"
              css={styles.jumpButton}
              onClick={handleJumpToBottom}
              aria-label={_t('Jump to most recent message')}
              data-testid="chat-jump-to-bottom"
            />
          </div>
        </Fade>
      </div>
    );
  }
);

ChatMessagesContainer.displayName = 'ChatMessagesContainer';

export default ChatMessagesContainer;
